import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  AfterViewInit,
  DestroyRef,
} from '@angular/core';
import { AuthDownloadService } from '../../shared/services/auth-download.service';
import { createArchiveDownloadCallbacks } from '../../shared/services/archive-download-feedback.util';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../store/app.state';
import * as GrantedAccessActions from '@states/granted-access/granted-access.actions';
import {
  BehaviorSubject,
  filter,
  from,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import * as GrantedAccessSelectors from '@states/granted-access/granted-access.selectors';
import {
  Archive,
  ArchiveStatus,
  ArchiveVideoFormat,
} from '@models/archive.model';
import { PreloaderColor } from '@enums/shared.enum';
import * as LocationModel from '../../locations/location.model';
import { Location } from '@angular/common';
import {
  AuthenticationService,
  SHARE_ACCESS_TOKEN_KEY,
} from '../../authentication/authentication.service';
import { GrantedAccessEffect } from '@effects/granted-access.effect';
import { ofType } from '@ngrx/effects';
import { GrantedAccessModel } from '@models/granted-access-model';
import { Comment, CommentEntityType, CommentRequest } from '@models/comment';
import { GrantedAccessType } from '@enums/granted-access.enum';
import { AlertEntry } from '../../development/alerts.service';
import { GoogleMapsService } from '../../services/google-maps.service';
import { extractPanicCoords, isPanicAlertEntry } from './panic-alert.util';
import { PlaybackPlayerService } from 'src/app/shared/playback-player/playback-player.service';
import { PlaybackPlayerComponent } from 'src/app/shared/playback-player/playback-player.component';
import { ArchiveEffect } from '@effects/archive.effect';
import moment from 'moment-timezone';
import { ARCHIVE_ON_ERROR_MIN } from '@consts/archive.conts';
import ArchiveDocument = Archive.ArchiveDocument;
import * as ArchiveActions from '@states/archive/archive.actions';
import { HttpStatusCode } from '@angular/common/http';
import { SocketEvents } from '../../socket/socket.model';
import { SocketMainService } from '../../socket/socket-main.service';
import * as ArchiveAction from '@states/archive/archive.actions';
import { SessionStorageService, ViewportService } from '@ui';
import { StorageBucket } from '@consts/storage-url.util';
import { ThumbnailsService } from '../../development/thumbnails.service';
import { UniversalLoginService } from '../../authentication/universal-login.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-granted-access',
  templateUrl: './granted-access.component.html',
  styleUrls: ['./granted-access.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GrantedAccessComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  public archiveStatus = ArchiveStatus;

  @ViewChild('playback')
  playback: PlaybackPlayerComponent;

  public selectInitialLoaded$: Observable<boolean> = this.store$.pipe(
    select(GrantedAccessSelectors.selectInitialLoaded),
  );
  public selectIsNotFound$: Observable<boolean> = this.store$.pipe(
    select(GrantedAccessSelectors.selectIsNotFound),
  );
  public isRecordProtected$: Observable<boolean> =
    this.grantedAccessEffect.checkGrantedAccess$.pipe(
      ofType(GrantedAccessActions.checkGrantedAccessProtected),
      tap(() => (this.isSubmit = false)),
      map((res) => res.isProtected),
    );

  public selectSharedArchive$: Observable<ArchiveDocument> = this.store$.pipe(
    select(GrantedAccessSelectors.selectSharedArchive),
  );
  public selectSharedLiveView$: Observable<GrantedAccessModel.SharedLiveView> =
    this.store$.pipe(select(GrantedAccessSelectors.selectSharedLiveView));
  public selectSharedPlayBack$: Observable<GrantedAccessModel.SharedPlayback> =
    this.store$.pipe(select(GrantedAccessSelectors.selectSharedPlayback));
  public selectSharedAlert$: Observable<AlertEntry> = this.store$.pipe(
    select(GrantedAccessSelectors.selectSharedAlert),
  );
  // null (unknown) until the alert loads — nothing should render for the ALERT
  // switch-case before we know which sub-component (panic vs. regular) it needs,
  // otherwise the regular <app-shared-alert> can briefly mount and fire its
  // camera-specific side effects for what turns out to be a panic alert.
  public isPanicAlert$: Observable<boolean | null> =
    this.selectSharedAlert$.pipe(
      map((alert) => (alert ? isPanicAlertEntry(alert) : null)),
    );
  public panicAddress$: Observable<string> = this.selectSharedAlert$.pipe(
    filter((alert) => !!alert && isPanicAlertEntry(alert)),
    switchMap((alert) => {
      const coords = extractPanicCoords(alert);
      return coords
        ? this.googleMapsService.reverseGeocode(coords.lat, coords.lng)
        : of(null);
    }),
  );

  public selectSelectedLocation$: Observable<LocationModel.LocationModel.LocationItem> =
    this.store$.pipe(select(GrantedAccessSelectors.selectSelectedLocation));
  public selectSharedNameAndDate$: Observable<{
    name: string;
    timestamp: number;
  }> = this.store$.pipe(select(GrantedAccessSelectors.selectSharedNameAndDate));
  public selectConfig$: Observable<GrantedAccessModel.GrantedAccessConfig> =
    this.store$.pipe(select(GrantedAccessSelectors.selectConfig));
  public selectType$: Observable<GrantedAccessType> = this.store$.pipe(
    select(GrantedAccessSelectors.selectType),
  );
  public selectAccessToken$: Observable<string> = this.store$.pipe(
    select(GrantedAccessSelectors.selectAccessToken),
  );
  public selectComments$: Observable<CommentRequest[]> = this.store$.pipe(
    select(GrantedAccessSelectors.selectComments),
  );

  public isEmbedded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  public keepRatio$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  public archiveUrl: string;
  public sharedArchive: ArchiveDocument;
  public grantedAccessTypes = GrantedAccessType;

  public loaderColor = PreloaderColor;
  public isPasswordVisible: boolean = false;
  public isSubmit: boolean = false;
  public password: string;
  public isExpired: boolean = false;
  @ViewChild('player') videoPlayer: ElementRef;
  public addCommentHide: boolean = true;

  private id: string;

  constructor(
    private playbackPlayerService: PlaybackPlayerService,
    private route: ActivatedRoute,
    private store$: Store<AppState>,
    public authenticationService: AuthenticationService,
    private grantedAccessEffect: GrantedAccessEffect,
    private location: Location,
    private archiveEffects: ArchiveEffect,
    private socketMainService: SocketMainService,
    private sessionStorageService: SessionStorageService,
    private thumbnailsService: ThumbnailsService,
    private toastrService: ToastrService,
    private translate: TranslateService,
    private authDownloadService: AuthDownloadService,
    private universalLoginService: UniversalLoginService,
    private destroyRef: DestroyRef,
    private googleMapsService: GoogleMapsService,
    public viewportService: ViewportService,
  ) {}

  public get now() {
    return Date.now();
  }

  ngAfterViewInit(): void {
    this.playbackPlayerService.playbackBase$
      .pipe(filter((res) => !!res.sessionId))
      .subscribe((res) => {
        this.playback?.reloadPlayer();
      });

    // Do not mount shared-archive until the private-storage map is applied,
    // otherwise getBucketBaseUrl() falls back to the share proxy.
    this.selectSharedArchive$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((archive) => !!archive),
        switchMap((archive) =>
          from(this.thumbnailsService.loadSharePrivateStorageMap()).pipe(
            map(() => archive),
          ),
        ),
      )
      .subscribe((res) => {
        this.archiveUrl = this.getCloudPreviewUri(res as ArchiveDocument);
        this.sharedArchive = res as ArchiveDocument;
      });
  }

  ngOnInit(): void {
    // Shared archive / alert URLs must use /storage-download/share/... + sharedToken even in incognito (no active org / tunnel).
    this.thumbnailsService.setShareMode(true);

    this.selectAccessToken$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((x) => !!x),
        take(1),
      )
      .subscribe(async (res) => {
        // Token must be in sessionStorage before this request (stored by the granted-access effect).
        await this.thumbnailsService.loadSharePrivateStorageMap();
        await this.socketMainService.connect(res);
        this.socketMainService
          .consume<{
            data: Archive.ArchiveDocument;
            error: string;
            statusCode: HttpStatusCode;
          }>(SocketEvents.edgeArchiveProgressNotification)
          .subscribe((res) => {
            this.store$.dispatch(
              ArchiveActions.archiveProgressSocketUpdate({
                archive: res.data,
                error: res.error,
                statusCode: res.statusCode,
              }),
            );
          });
      });

    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.id = res['id'];
        this.store$.dispatch(
          GrantedAccessActions.checkGrantedAccess({ id: this.id }),
        );
      });

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res['embedded'] === 'true') {
          this.isEmbedded$.next(true);
        }
        if (res['ratio'] === 'true') {
          this.keepRatio$.next(true);
        }
        // if (res['token']) {
        //   this.sessionStorageService.setItem(SHARE_ACCESS_TOKEN_KEY, res['token']);
        // }
      });

    this.selectSharedPlayBack$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((res) => !res),
      )
      .subscribe((res) => {
        this.initBaseOnEntityType(res);
      });

    this.grantedAccessEffect.checkGrantedAccess$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(GrantedAccessActions.checkGrantedAccessSuccess),
      )
      .subscribe(() => {
        this.loadPage();
      });

    /**
     * Needs to generate download url for header.
     */
    this.selectSharedArchive$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((res) => !!res),
      )
      .subscribe((res) => {
        const diff = moment().diff(moment(res.progressUpdateTime * 1000));
        const duration = moment.duration(diff);
        if (
          res.status === ArchiveStatus.UPLOADING &&
          duration.asMinutes() > ARCHIVE_ON_ERROR_MIN
        ) {
          this.store$.dispatch(
            GrantedAccessActions.updateSharedArchive({
              archive: { ...res, status: ArchiveStatus.ERROR },
            }),
          );
        }
      });

    this.archiveEffects.archiveProgressSocketUpdate$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res.statusCode !== HttpStatusCode.NotFound) {
          this.store$.dispatch(GrantedAccessActions.updateSharedArchive(res));
        }
      });

    this.archiveEffects.downloadArchive$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(ArchiveAction.downloadArchiveSuccess),
      )
      .subscribe((res) => {
        this.authDownloadService.download(
          res.url,
          res.name,
          createArchiveDownloadCallbacks(this.toastrService, this.translate),
        );
      });
  }

  public signIn(): void {
    this.universalLoginService.redirectToLogin();
  }

  public back() {
    this.location.back();
  }

  public submitPassword() {
    this.isSubmit = true;
    this.store$.dispatch(
      GrantedAccessActions.checkGrantedAccess({
        id: this.id,
        body: { password: this.password },
      }),
    );
  }

  public commentAdd(comment: Comment) {
    this.store$.dispatch(
      GrantedAccessActions.addComment({
        comment: {
          ...comment,
          entityId: this.id,
          entityType: CommentEntityType.grantedAccess,
        },
      }),
    );
    this.addCommentHide = !this.addCommentHide;
  }

  public cancelComment() {
    this.addCommentHide = true;
  }

  initBaseOnEntityType(entity: GrantedAccessModel.SharedPlayback) {
    if (!!entity?.alertTime) {
      this.playbackPlayerService.setTime(entity.alertTime);
      // setTimeout(() => {
      //
      //   this.playbackPlayerService.initTimeLine(entity.alertTime);
      // }, 2000)
    }
  }

  public downloadArchive() {
    this.store$.dispatch(
      ArchiveActions.downloadArchive({
        archive: this.sharedArchive,
        sharedToken: true,
      }),
    );
  }

  private loadPage(): void {
    this.store$.dispatch(GrantedAccessActions.getConfig({ id: this.id }));
    this.store$.dispatch(GrantedAccessActions.viewShared({ id: this.id }));
    this.store$.dispatch(GrantedAccessActions.getComments({ id: this.id }));
  }

  public getCloudPreviewUri(archive: ArchiveDocument) {
    if (archive.isClip) {
      const baseUrl = `${this.thumbnailsService.getBucketBaseUrl(StorageBucket.Videos, true, false, archive.edgeId)}/org/${archive.orgId}/edge/${archive.edgeId}/camera/${archive.cameraId}/session/${archive.sessionId}/preview`;
      return `${baseUrl}/alert_${archive.sessionId}.mp4`;
    }
    const archiveBase = this.thumbnailsService.getBucketBaseUrl(
      StorageBucket.Archives,
      true,
      false,
      archive.edgeId,
    );
    const ext =
      archive?.status === ArchiveStatus.COMPLETED &&
      archive?.videoFormat === ArchiveVideoFormat.HLS
        ? 'm3u8'
        : 'mp4';
    return `${archiveBase}/org/${archive.orgId}/edge/${archive.edgeId}/camera/${archive.cameraId}/session/${archive.sessionId}/preview/${archive.name}.${ext}`;
  }

  // ngOnDestroy only fires on in-app (Angular router) navigation away from this page.
  // Typing a new URL, refreshing, or closing the tab unloads the document directly and
  // skips Angular's lifecycle entirely, so the shared token would otherwise survive in
  // sessionStorage and get picked up by every later request as if still in share mode.
  @HostListener('window:beforeunload')
  public clearSharedToken(): void {
    this.sessionStorageService.removeItem(SHARE_ACCESS_TOKEN_KEY);
  }

  public ngOnDestroy() {
    this.thumbnailsService.setShareMode(false);
    this.clearSharedToken();
  }

  public get isMobile(): boolean {
    return this.viewportService.isMobile();
  }
}
